import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount, { AddAccountParameter } from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { AccountModel, CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useForm } from "@tLinc/CustomHooks/useForm";
import { formatPhoneNumber } from "@tLinc/Utilities/FormatPhoneNumber";
import { ValidatorConfigType } from "@tLinc/Utilities/Validate";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    addAccount,
    signIn,
    loadCurrentCountries,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl: getCreateAccountReturnUrl(state),
        countries: getCurrentCountries(state),
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        isAuthenticated: state.context.session.isAuthenticated,
        homePageUrl: getHomePageUrl(state),
        reCaptchaEnabledForCreateAccount: getSettingsCollection(state).websiteSettings.reCaptchaEnabledForCreateAccount,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface FormValues {
    accountNumber?: string;
    salesPersonName?: string;
    registrationType: any;
    businessType: any;
    ProductServiceInterest?: string;
    companyName: string;
    email: string;
    firstName: string;
    lastName: string;
    PhoneNumber: string;
    address1: string;
    address2: string;
    country: CountryModel | undefined;
    state: StateModel | undefined;
    city: string;
    zip: string;
}

/* Create Valition Config as per validation required for each field */
const validatorConfig: ValidatorConfigType<FormValues> = {
    accountNumber: {
        label: "Account Number",
    },
    salesPersonName: {
        label: "Salesperson Name",
    },
    registrationType: {
        label: "Select Registration Type",
    },
    businessType: {
        label: "Select Bussiness Type",
    },
    ProductServiceInterest: {
        required: false,
        label: "Select Product Service Interest",
    },
    companyName: {
        required: true,
        label: "Company Name",
        minLength: 2,
        requiredMsg: "Company Name is required.",
    },
    email: {
        required: true,
        regex: /\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/,
        label: "Email",
        regexMsg: "Please enter valid email.",
        requiredMsg: "Email Address is required.",
    },
    firstName: {
        required: true,
        label: "First Name",
        minLength: 2,
        requiredMsg: "First Name is required.",
    },
    lastName: {
        required: true,
        label: "Last Name",
        minLength: 2,
        requiredMsg: "Last Name is required.",
    },
    PhoneNumber: {
        required: true,
        regex: /^\+?[0-9]{6,15}$/,
        label: "Phone Number",
        minLength: 2,
        requiredMsg: "Phone Number is required.",
    },
    address1: {
        required: true,
        label: "Address Line",
        requiredMsg: "Address 1 is required.",
    },
    address2: {
        label: "Address Line2 (Optional)",
    },
    country: {
        required: true,
        label: "Country",
        requiredMsg: "Country is required.",
    },
    state: {
        required: true,
        label: "State",
        requiredMsg: "State is required.",
    },
    city: {
        required: true,
        label: "City",
        requiredMsg: "City is required.",
    },
    zip: {
        required: true,
        label: "Postal Code",
        requiredMsg: "Postal Code is required.",
    },
};

const initialFormValues = {
    registrationType: undefined,
    businessType: undefined,
    companyName: "",
    email: "",
    firstName: "",
    lastName: "",
    PhoneNumber: "",
    address1: "",
    address2: "",
    country: undefined,
    state: undefined,
    city: "",
    zip: "",
};

/* initialErrorState values of all properties should be empty string */
const initialErrorState = {
    registrationType: "",
    businessType: "",
    companyName: "",
    email: "",
    firstName: "",
    lastName: "",
    PhoneNumber: "",
    address1: "",
    country: "",
    state: "",
    city: "",
    zip: "",
};

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    requiredText: GridItemProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    textField?: TextFieldPresentationProps;
    activationInstructionGridItem?: GridItemProps;
    activationInstructionText?: TypographyPresentationProps;
    activationSuccessWrapper?: InjectableCss;
    activationSuccessTitleText?: TypographyPresentationProps;
    activationSuccessMessageText?: TypographyPresentationProps;
    activationSuccessHomeButton?: ButtonPresentationProps;
}

export const createAccountStyles: CreateAccountStyles = {
    requiredText: {
        css: css`
            padding-left: 0px;
            padding-bottom: 0px;
        `,
    },
    mainGridContainer: {
        css: css`
            margin: 0 auto;
            > ${GridOffset} {
                padding: 0 1.5rem;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            margin-bottom: 4rem;
            position: relative;
            ${GridItemStyle} {
                padding-bottom: 0px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )} /* stylelint-disable-line declaration-block-single-line-max-declarations */

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 2rem !important;
                            margin-top: -32px !important;
                        `,
                    ],
                    "max",
                )}

            ${StyledWrapper} {
                border: 1px #ccc solid;
                padding: 10px;
            }
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
        css: css`
            font-size: 18px;
        `,
    },
    submitButton: {
        css: css`
            min-width: 200px;
        `,
    },
    textField: {},
    activationInstructionGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    activationSuccessWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 50%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    activationSuccessTitleText: {
        variant: "h3",
    },
    activationSuccessHomeButton: {
        css: css`
            align-self: flex-end;
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const CreateAccount = ({
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    loadCurrentCountries,
    shouldLoadCountries,
    countries,
    isAuthenticated,
    homePageUrl,
    reCaptchaEnabledForCreateAccount,
}: Props) => {
    const history = useHistory();
    const {
        formState,
        formErrors,
        isSubmitDisabled,
        fieldChangeHandler,
        updateFormState,
        updateFormErrors,
        validateField,
    } = useForm<FormValues>({
        formValues: initialFormValues,
        initialErrorState: initialErrorState as { [key in keyof FormValues]?: string },
        validatorConfig,
    });
    const userNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_UserName_Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");

    const [email, setEmail] = useState("");
    const [userName, setUserName] = useState("");
    const [userNameError, setUserNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : userNameRequiredFieldMessage,
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<ReactNode>("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isActivationEmailSent, setIsActivationEmailSent] = useState(false);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const [registration, setRegistration] = useState([
        {
            value: "existing_acc",
            name: "Register and join an existing business account",
        },
        {
            value: "new_acc",
            name: "Register and create a new business account",
        },
    ]);
    const [bussinessData, setBussinessData] = useState([
        {
            value: "industrial_fastening",
            name: "Industrial Fastening",
        },
        {
            value: "industrial_packaging",
            name: "Industrial Packaging",
        },
        {
            value: "direct_construction",
            name: "Direct Construction",
        },
        {
            value: "wholesale_yard",
            name: "Wholesale Dealer / Lumber Yard ",
        },
    ]);
    const [accountNoFlag, setAccountNoFlag] = useState(false);
    const [salesNameFlag, setSalesNameFlag] = useState(false);

    useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, []);

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        useEmailAsUserName,
        requireActivateAccount,
    } = accountSettings;

    const validateUserName = (name: string) => {
        const errorMessage = !name && !useEmailAsUserName ? userNameRequiredFieldMessage : "";
        setUserNameError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(emailError || userNameError);
    };

    const emailChangeHandler = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        setEmail(email);
    };

    const userChangeHandler = (name: string) => {
        validateUserName(name);
        setUserName(name);
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const { name, value } = event.target;
        const country = countries?.find(c => c.id === value);
        updateFormState(() => ({ ...formState, country, state: undefined }));
        const errors = { ...formErrors };
        if (!country?.states || country.states.length === 0) {
            delete errors.state;
        } else {
            errors.state = "";
        }
        updateFormErrors(() => errors);
        validateField({ name, value });
    };

    const handelRegisterChange: SelectProps["onChange"] = event => {
        const { name, value } = event.target;
        const registrationData = registration?.find(c => c.value === value);
        updateFormState(() => ({ ...formState, registrationType: registrationData?.name || "" }));
        validateField({ name, value });
        if (value === "existing_acc") {
            setSalesNameFlag(false);
            setAccountNoFlag(true);
        } else {
            setAccountNoFlag(false);
            setSalesNameFlag(true);
        }
    };

    const handeleBussinessChange: SelectProps["onChange"] = event => {
        const { name, value } = event.target;
        const bussinessDatas = bussinessData?.find(c => c.value === value);
        updateFormState(() => ({ ...formState, businessType: bussinessDatas?.name || "" }));
        validateField({ name, value });
    };
    const handeleProductServiceInterestChange: SelectProps["onChange"] = event => {};

    const handleStateChange: SelectProps["onChange"] = event => {
        const { name, value } = event.target;
        const state = formState.country?.states?.find(s => s.id === value);
        validateField({ name, value });
        updateFormState(() => ({ ...formState, state }));
    };

    const onSuccessHandler = (account?: AccountModel) => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
        history.push("/thankyou");
    };

    const submitHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        if (isSubmitDisabled) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsSubmitting(false);
            return;
        }

        const userAccountParams = {
            email: formState.email,
            password: "",
            isSubscribed,
            isGuest: isAuthenticated,
            firstName: formState.firstName,
            lastName: formState.lastName,
            requiresActivation: true,
            properties: {
                PhoneNumber: formState.PhoneNumber,
                CompanyName: formState.companyName,
                Address1: formState.address1,
                Address2: formState.address2,
                Country: formState.country!.id,
                CountryName: formState.country!.name,
                States: formState.state?.id,
                StateName: formState.state?.name,
                City: formState.city,
                PostalCode: formState.zip,
                registrationType: formState.registrationType,
                businessType: formState.businessType,
                ProductServiceInterest: formState.ProductServiceInterest,
                accountNumber: formState.accountNumber,
                salesPersonName: formState.salesPersonName,
            },
        };

        if (!userAccountParams.properties.States) {
            delete userAccountParams.properties.States;
            delete userAccountParams.properties.StateName;
        }

        addAccount({
            ...userAccountParams,
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC

                    (this as unknown as any).onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC

                    (this as unknown as any).onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as unknown as AddAccountParameter);
    };

    const homeClickHandler = () => {
        history.push(homePageUrl);
    };

    if (isActivationEmailSent) {
        return (
            <StyledWrapper {...styles.activationSuccessWrapper}>
                <Typography as="h2" {...styles.activationSuccessTitleText}>
                    {translate("Check your email")}
                </Typography>
                <Typography {...styles.activationSuccessMessageText}>
                    {siteMessage("CreateNewAccountInfo_AccountActivation_SuccessMessage")}
                </Typography>
                <Button
                    {...styles.activationSuccessHomeButton}
                    onClick={homeClickHandler}
                    data-test-selector="createAccount_BackToHomePage"
                >
                    {translate("Home")}
                </Button>
            </StyledWrapper>
        );
    }

    const isSubmitEnabled = validateSubmitEnabled();

    const phoneNumberChangeHandler = (name: string, value: string) => {
        const phoneNum = formatPhoneNumber(value);
        validateField({ name, value: phoneNum });
        updateFormState({ ...formState, [name]: phoneNum });
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.requiredText} width={12}>
                <Typography color="primary">Required*</Typography>
            </GridItem>
            <GridItem {...styles.userGridItem}>
                <GridContainer {...styles.userGridContainer}>
                    <GridItem width={12}>
                        <Select
                            label="Registration Type"
                            name="registrationType"
                            onChange={handelRegisterChange}
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            // value={formState.country?.id || ""}
                            // error={!!formErrors.country && formErrors.country}
                            required
                        >
                            <option value="">{translate("Select Registration Type")}</option>
                            {registration?.map(reg => (
                                <option key={reg.value} value={reg.value}>
                                    {reg.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>

                    {salesNameFlag && (
                        <GridItem width={12}>
                            <TextField
                                {...styles.textField}
                                label={translate("Enter salesperson Name")}
                                name="salesPersonName"
                                value={formState.salesPersonName}
                                data-test-selector="createAccount_salesPersonName"
                                onChange={e =>
                                    fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                                }
                            />
                        </GridItem>
                    )}

                    {accountNoFlag && (
                        <GridItem width={12}>
                            <TextField
                                {...styles.textField}
                                label={translate("Enter Account Number")}
                                name="accountNumber"
                                value={formState.accountNumber}
                                data-test-selector="createAccount_accountNo"
                                onChange={e =>
                                    fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                                }
                            />
                        </GridItem>
                    )}

                    <GridItem width={12}>
                        <Select
                            label="businessType"
                            name="businessType"
                            onChange={handeleBussinessChange}
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            required
                        >
                            <option value="">{translate("Select Business Type")}</option>
                            {bussinessData?.map(bussiness => (
                                <option key={bussiness.value} value={bussiness.value}>
                                    {bussiness.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Enter Product/Service Interests")}
                            name="ProductServiceInterest"
                            value={formState.ProductServiceInterest}
                            data-test-selector="createAccount_ProductServiceInterest"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Company Name")}
                            name="companyName"
                            value={formState.companyName}
                            data-test-selector="createAccount_Company"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.companyName && formErrors.companyName}
                            iconProps={{
                                color: "danger",
                                src: formErrors.companyName ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("First Name")}
                            name="firstName"
                            value={formState.firstName}
                            data-test-selector="createAccount_firstName"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.firstName && formErrors.firstName}
                            iconProps={{
                                color: "danger",
                                src: formErrors.firstName ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Last Name")}
                            name="lastName"
                            value={formState.lastName}
                            data-test-selector="createAccount_lastName"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.lastName && formErrors.lastName}
                            iconProps={{
                                color: "danger",
                                src: formErrors.lastName ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Phone Number")}
                            name="PhoneNumber"
                            value={formState.PhoneNumber}
                            data-test-selector="createAccount_PhoneNumber"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.PhoneNumber && formErrors.PhoneNumber}
                            iconProps={{
                                color: "danger",
                                src: formErrors.PhoneNumber ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            type="email"
                            name="email"
                            label="Email Address"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            value={formState.email}
                            data-test-selector="createAccount_email"
                            maxLength={256}
                            error={!!formErrors.email && formErrors.email}
                            iconProps={{
                                color: "danger",
                                src: formErrors.email ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Address 1")}
                            name="address1"
                            value={formState.address1}
                            data-test-selector="createAccount_address1"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.address1 && formErrors.address1}
                            iconProps={{
                                color: "danger",
                                src: formErrors.address1 ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Address 2")}
                            name="address2"
                            value={formState.address2}
                            data-test-selector="createAccount_address2"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <Select
                            label="Country"
                            name="country"
                            onChange={handleCountryChange}
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            value={formState.country?.id || ""}
                            error={!!formErrors.country && formErrors.country}
                            required
                        >
                            <option value="">{translate("Select Country")}</option>
                            {countries?.map(co => (
                                <option key={co.id} value={co.id}>
                                    {co.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    {formState?.country?.states && formState?.country?.states?.length > 0 ? (
                        <GridItem width={12}>
                            <Select
                                label="State"
                                name="state"
                                value={formState.state?.id || ""}
                                onChange={handleStateChange}
                                onBlur={e =>
                                    validateField({ name: e.currentTarget.name, value: e.currentTarget.value })
                                }
                                error={!!formErrors.state && formErrors.state}
                                required
                            >
                                <option value="">{translate("Select State")}</option>
                                {formState?.country?.states?.map(st => (
                                    <option key={st.id} value={st.id}>
                                        {st.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    ) : null}
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("City")}
                            name="city"
                            value={formState.city}
                            data-test-selector="createAccount_city"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.city && formErrors.city}
                            iconProps={{
                                color: "danger",
                                src: formErrors.city ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem width={12}>
                        <TextField
                            {...styles.textField}
                            label={translate("Postal Code")}
                            name="zip"
                            value={formState.zip}
                            data-test-selector="createAccount_zip"
                            onChange={e =>
                                fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })
                            }
                            onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                            error={!!formErrors.zip && formErrors.zip}
                            iconProps={{
                                color: "danger",
                                src: formErrors.zip ? AlertTriangle : undefined,
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem {...styles.subscriptionGridItem}>
                        <CheckboxGroup>
                            <Checkbox
                                onChange={(e, value) => setIsSubscribed(value)}
                                checked={isSubscribed}
                                data-test-selector="createAccount_Subscription"
                            >
                                {siteMessage("SignIn_Sign_Up_Newsletter")}
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                    {errorMessage && (
                        <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                            <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                        </GridItem>
                    )}
                    {reCaptchaEnabledForCreateAccount && (
                        <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                        </GridItem>
                    )}
                    <GridItem {...styles.submitGridItem}>
                        <Button
                            {...styles.submitButton}
                            type="submit"
                            disabled={isSubmitDisabled || isSubmitting}
                            data-test-selector="createAccount_createButton"
                            onClick={submitHandler}
                        >
                            {translate("Request Account")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
